import { ApiService } from '../apiService'
import type {
  SaveTripRequest,
  ShareTripRequest,
  TripAdventureState,
  TripDto,
  TripInviteDto,
  TripItineraryDto,
  TripLegsRequest,
  TripLegsResponse,
  UpdateTripRequest,
  TripSuggestionsRequest,
  TripSuggestionsResponse,
} from '../types/trips'

/**
 * Trip planner — a paid capability.
 *
 * Every call can answer **402** with `{ reason: 'trip_planner_not_included' }`. That is not an
 * error to retry: it means the plan does not include the planner. Unlike the mobile app, the site
 * can offer the upgrade directly.
 */
export const tripsApi = {
  getTrips: () => ApiService.get<TripDto[]>('/api/me/trips'),

  getTrip: (tripId: string) => ApiService.get<TripDto>(`/api/me/trips/${tripId}`),


  /** Change part of a trip. Editors and the owner. */
  updateTrip: (tripId: string, body: UpdateTripRequest) =>
    ApiService.patch<TripDto>(`/api/me/trips/${tripId}`, body),

  /** Put an adventure on a trip, or take it off the map. */
  setAdventure: (tripId: string, adventureId: string, state: TripAdventureState) =>
    ApiService.put<TripDto>(`/api/me/trips/${tripId}/adventures/${adventureId}`, { state }),

  /** Forget the decision entirely, handing the adventure back to the corridor. */
  clearAdventure: (tripId: string, adventureId: string) =>
    ApiService.delete<TripDto>(`/api/me/trips/${tripId}/adventures/${adventureId}`),

  shareTrip: (tripId: string, body: ShareTripRequest) =>
    ApiService.post<TripDto>(`/api/me/trips/${tripId}/shares`, body),

  unshareTrip: (tripId: string, shareId: string) =>
    ApiService.delete<TripDto>(`/api/me/trips/${tripId}/shares/${shareId}`),

  /** Step off a trip somebody shared with you. */
  leaveTrip: (tripId: string) =>
    ApiService.post<void>(`/api/me/trips/${tripId}/leave`, {}),

/** Trips somebody wants you on, that you have not answered. */
  getInvites: () => ApiService.get<TripInviteDto[]>('/api/me/trips/invites'),

  /** Accept an invitation — only now does the trip appear in your list. */
  acceptInvite: (shareId: string) =>
    ApiService.post<TripDto>(`/api/me/trips/invites/${shareId}/accept`, {}),

  declineInvite: (shareId: string) =>
    ApiService.post<TripDto>(`/api/me/trips/invites/${shareId}/decline`, {}),

  saveTrip: (request: SaveTripRequest) => ApiService.post<TripDto>('/api/me/trips', request),

  deleteTrip: (tripId: string) => ApiService.delete<void>(`/api/me/trips/${tripId}`),

  /** Activating stands any other trip down — only one is ever active. */
  setActive: (tripId: string, active: boolean) =>
    ApiService.post<TripDto>(`/api/me/trips/${tripId}/activate?active=${active}`, {}),

  /**
   * The trip's stops in visiting order, with leg times, arrivals, eligibility and opening hours.
   * Costs routing calls server-side: fetch it when the trip opens or changes, not per render.
   */
  getItinerary: (tripId: string) =>
    ApiService.get<TripItineraryDto>(`/api/me/trips/${tripId}/itinerary`),

  /** Road distance and time between the trip's own points ('start', 'end', 'wp:N', adventure id). */
  getLegs: (tripId: string, body: TripLegsRequest) =>
    ApiService.post<TripLegsResponse>(`/api/me/trips/${tripId}/legs`, body),

  /**
   * What is worth stopping for along a route.
   *
   * Costs a routing call plus a spatial scan server-side, so it runs when the traveller asks —
   * never on every keystroke.
   */
  getSuggestions: (request: TripSuggestionsRequest) =>
    ApiService.post<TripSuggestionsResponse>('/api/adventures/trip-suggestions', request),
}
